import { useEffect, useState } from 'react';
import FlipNumber from './FlipNumber';
import { SOUND_KANJI, type SoundKanjiEntry } from '../data/soundKanji';

type Phase = 'loading' | 'revealed' | 'closing' | 'done';

// 音にまつわる漢字リストから、毎回ランダムに1字を選ぶ
function pickRandomKanji(): SoundKanjiEntry {
  const index = Math.floor(Math.random() * SOUND_KANJI.length);
  return SOUND_KANJI[index];
}

export default function KanjiHeroSection() {
  // マウント時に一度だけ抽選（サイトを開くたびに1字が決まる）
  const [entry] = useState<SoundKanjiEntry>(() => pickRandomKanji());
  const [phase, setPhase] = useState<Phase>('loading');

  // ローディングカウンター(約1.9s) → 漢字がドンと登場（以後はクリック待ち）
  useEffect(() => {
    const timer = setTimeout(() => setPhase('revealed'), 1900);
    return () => clearTimeout(timer);
  }, []);

  // ゲート表示中は背景スクロールをロック
  useEffect(() => {
    if (phase === 'done') {
      document.body.style.overflow = '';
      return;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [phase]);

  // クリック/タップでゲートを閉じる：漢字がズームアウト → 通常ヒーローへ
  const handleEnter = () => {
    if (phase !== 'revealed') return;
    setPhase('closing');
    setTimeout(() => setPhase('done'), 760);
  };

  // 漢字ごとの配色をCSSに変換
  const overlayBackground = `radial-gradient(circle at 50% 44%, ${entry.glow} 0%, ${entry.bg1} 46%, ${entry.bg2} 100%)`;
  const inkStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(180deg, ${entry.ink} 0%, ${entry.ink2} 100%)`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
  };

  return (
    <>
      {(phase === 'loading' || phase === 'revealed' || phase === 'closing') && (
        <div
          role="button"
          tabIndex={0}
          aria-label="タップしてサイトへ進む"
          onClick={handleEnter}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleEnter();
            }
          }}
          className={`fixed inset-0 z-[10000] flex items-center justify-center overflow-hidden opening-overlay cursor-pointer ${
            phase === 'closing' ? 'is-zooming' : ''
          }`}
          style={{ backgroundColor: entry.bg2 }}
        >
          {/* 漢字ごとの色に染まる背景 */}
          <div className="absolute inset-0" style={{ background: overlayBackground }} />
          {/* ごく薄いグレインで奥行きを出す */}
          <div className="grain-overlay" aria-hidden="true" />

          {phase === 'loading' ? (
            <div className="relative z-10 flex flex-col items-center">
              <FlipNumber
                value={100}
                suffix="%"
                duration={1600}
                className="text-7xl md:text-9xl font-extralight text-white/95"
              />
              <span className="mt-12 block h-px w-20 bg-white/15 overflow-hidden" aria-hidden="true">
                <span
                  className="block h-full origin-left"
                  style={{ backgroundColor: entry.accent, animation: 'loading-progress 1.6s ease-out both' }}
                />
              </span>
              <span className="mt-8 text-[10px] font-normal tracking-[0.55em] text-white/40 uppercase">
                Loading
              </span>
            </div>
          ) : (
            <>
              {/* 衝撃波リング（テーマ色） */}
              <span
                className="absolute rounded-full"
                style={{
                  width: '54vmin',
                  height: '54vmin',
                  border: `1px solid ${entry.accent}55`,
                  animation: 'shockwave 1.2s ease-out 0.05s both',
                }}
              />

              {/* 中央の漢字と一言メモ */}
              <div className="relative z-10 flex flex-col items-center px-6 text-center">
                <span
                  className="mb-6 text-[10px] font-normal uppercase"
                  style={{ color: `${entry.ink}cc`, letterSpacing: '0.5em' }}
                >
                  Sound Kanji
                </span>

                <div style={{ animation: 'kanji-breathe 6s ease-in-out infinite' }} aria-hidden="true">
                  <div
                    className="font-kanji leading-none select-none"
                    style={{
                      fontSize: 'min(40vw, 44vh)',
                      fontWeight: 600,
                      ...inkStyle,
                      animation: 'kanji-slam 0.7s cubic-bezier(0.22,1,0.36,1) both',
                      ['--slam-scale' as string]: 1.16,
                    } as React.CSSProperties}
                  >
                    {entry.kanji}
                  </div>
                </div>

                <span
                  className="mt-8 mb-5 block h-px w-16"
                  style={{ backgroundColor: `${entry.accent}99` }}
                  aria-hidden="true"
                />
                <p
                  className="max-w-md text-sm md:text-base font-normal leading-relaxed text-white/65 kanji-fade"
                  style={{ animationDelay: '0.5s' }}
                >
                  {entry.memo}
                </p>
              </div>

              {/* クリックを促すヒント */}
              <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3 gate-hint">
                <span className="flex items-center gap-2 text-white/70 text-xs tracking-[0.35em] uppercase">
                  <span className="w-8 h-[1px]" style={{ backgroundColor: `${entry.accent}99` }} />
                  Click to enter
                  <span className="w-8 h-[1px]" style={{ backgroundColor: `${entry.accent}99` }} />
                </span>
                <span className="text-white/40 text-[11px] tracking-[0.2em]">タップして進む</span>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
